/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import { TextField, Autocomplete } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { GdprUtils } from '@cyberint/common-utils';
import { CrumbContent, CrumbWrapper } from '../BreadCrumb/styled';
import * as S from '../NavMenu/styled';

const emptyArr = [];

export const RegionSelectorBase = ({
  breadCrumbMode,
  testId,
  isCandyBar,
  user,
  region,
  setRegion,
  availableRegions = emptyArr
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchParams] = useSearchParams();
  const urlRegion = searchParams.get('region');

  // Controlled by the `region` prop (the shared regionState atom) — NOT local
  // state — so the selector reflects programmatic region changes too (e.g. a
  // ?region= deep link switching the active region/tenant), not just manual picks.
  const currValue = region || GdprUtils.normalizeRegion(user.customer.region);

  const Wrapper = breadCrumbMode ? S.TenantBreadcrumbWrapper : S.TenantsWrapper;

  const options = availableRegions.map(r => ({
    label: `${r} Region`,
    id: GdprUtils.normalizeRegion(r)
  }));

  const selectedOption = options.find(option => option.id === GdprUtils.normalizeRegion(currValue)) || null;

  const handleSelect = (event, newValue) => {
    setRegion(GdprUtils.normalizeRegion(newValue.id));
  };

  useEffect(() => {
    if (!region) {
      setRegion(GdprUtils.normalizeRegion(user.customer.region));
    }
  }, []);

  // Apply a `?region=` deep link to the active region, but only for a region the
  // user has access to (availableRegions), ignoring unknown/unauthorized values.
  useEffect(() => {
    if (!GdprUtils.isGdprRegion(urlRegion)) return;
    const normalized = GdprUtils.normalizeRegion(urlRegion);
    const isAllowedRegion = availableRegions.some(r => GdprUtils.normalizeRegion(r) === normalized);
    if (!isAllowedRegion) return;
    if (GdprUtils.normalizeRegion(currValue) !== normalized) {
      setRegion(normalized);
    }
  }, [urlRegion, availableRegions, currValue, setRegion]);

  if (options.length <= 1) {
    return null;
  }

  return (
    <CrumbWrapper $border $isCandyBar={isCandyBar}>
      <CrumbContent>
        <Wrapper>
          <Autocomplete
            blurOnSelect
            onChange={handleSelect}
            value={selectedOption}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            data-testid={testId}
            id="combo-box-demo"
            disablePortal
            disableClearable
            options={options}
            title={currValue}
            sx={{ width: 80, minWidth: 40, maxWidth: 250 }}
            renderInput={params => <TextField variant="standard" {...params} />}
            popupIcon={<KeyboardArrowDownRounded />}
            renderOption={(props, option) => (
              <li {...props}>
                <S.TenantOption>{option.label}</S.TenantOption>
              </li>
            )}
          />
        </Wrapper>
      </CrumbContent>
    </CrumbWrapper>
  );
};

RegionSelectorBase.propTypes = {
  breadCrumbMode: PropTypes.bool,
  testId: PropTypes.string,
  isCandyBar: PropTypes.bool,
  user: PropTypes.object.isRequired,
  region: PropTypes.string,
  setRegion: PropTypes.func.isRequired,
  availableRegions: PropTypes.array
};
